/* eslint-disable max-lines */
import { API_ENDPOINT, ENV, GATEWAY_SECRETS, IS_DEVELOPMENT } from 'settings';
import * as Sentry from '@sentry/browser';
import { configService } from './ConfigService';
import axios, { AxiosResponse } from 'axios';
import { cartService } from './CartService';

export interface IHashConfig {
  id: number | string;
  type: 'mercadopago' | 'iugu' | 'pagarme';
  key: string;
}

export interface ICardInfo {
  email: string;
  number: string;
  month: string;
  year: string;
  name: string;
  cvv: string;
  document: string;
}

export interface IVaultHashResult {
  token: string;
  user: string;
  reference: string;
}

export interface IHashResult {
  id: number | string;
  hash: string;
}

const WAIT_FOR_CARD_MAX_TIME = 20000;

class CardHashService {
  public constructor(private readonly configs: IHashConfig[]) {}

  public async generateHashes(cardInfo: ICardInfo) {
    const productConfig = await configService.getConfig().take(1).toPromise();

    const groupsMap = this.configs.reduce((acc, item) => {
      if (!productConfig.enablePaymentWithoutLimit && String(item.id).match('WITHOUT_LIMIT') !== null) {
        return acc;
      }

      acc[item.type] = acc[item.type] || [];

      acc[item.type].push(item);

      return acc;
    }, {} as { [key: string]: IHashConfig[] });

    const groups = Object.values(groupsMap);

    const promises = groups.map((group) => this.generateHashByGroups(group, cardInfo));

    const results = await Promise.all(promises);

    const tokens = results.flatMap((r) => r);

    return tokens.reduce((acc, item) => {
      acc[item.id] = item.hash;
      return acc;
    }, {} as { [key: number]: string });
  }

  public async generateVaultHash(cardInfo: ICardInfo): Promise<IVaultHashResult | undefined> {
    try {
      const { transactionKey } = await cartService.getCart().take(1).toPromise();

      const {
        data: { secret, url }
      }: AxiosResponse = await axios.post(`${API_ENDPOINT}/vault/capture`, {
        traKey: transactionKey
      });

      const PCI_REFERENCE = 'eduzz-pci';
      const {
        data: { token, reference, user }
      } = await axios.post(
        `${url}?reference=${PCI_REFERENCE}`,
        {
          // eslint-disable-next-line camelcase
          card_holder: cardInfo.name,
          // eslint-disable-next-line camelcase
          card_number: cardInfo.number,
          // eslint-disable-next-line camelcase
          expiry_month: +cardInfo.month,
          // eslint-disable-next-line camelcase
          expiry_year_full: +`20${cardInfo.year}`,
          // eslint-disable-next-line camelcase
          expiry_year: +cardInfo.year,
          cvv: cardInfo.cvv
        },
        {
          headers: {
            'X-PCIVault-Capture-Secret': secret
          }
        }
      );

      return {
        token,
        user,
        reference
      };
    } catch (error) {
      Sentry.withScope(() => {
        Sentry.captureException(error);
      });

      return undefined;
    }
  }

  private async generateHashByGroups(group: IHashConfig[], cardInfo: ICardInfo) {
    const results: IHashResult[] = [];

    for (let config of group) {
      const result = await this.generateHash(config, cardInfo);
      results.push(result);
    }

    return results;
  }

  private async generateHash(config: IHashConfig, cardInfo: ICardInfo): Promise<IHashResult> {
    switch (config.type) {
      case 'mercadopago':
        return this.generateHashMercadoPago(config, cardInfo);
      case 'iugu':
        return this.generateHashIugu(config, cardInfo);
      case 'pagarme':
        return this.generateHashPagarme(config, cardInfo);
      default:
        return {
          id: 0,
          hash: ''
        };
    }
  }

  private async generateHashMercadoPago(config: IHashConfig, cardInfo: ICardInfo): Promise<IHashResult> {
    if (!window.MercadoPago) {
      return {
        id: config.id,
        hash: ''
      };
    }

    const payload = {
      email: cardInfo?.email,
      cardNumber: cardInfo?.number,
      securityCode: cardInfo?.cvv,
      expirationDate: `${cardInfo?.month}/${cardInfo?.year}`,
      cardExpirationMonth: cardInfo?.month,
      cardExpirationYear: `20${cardInfo?.year}`,
      cardholderName: cardInfo?.name,
      identificationType: (cardInfo?.document || '').length === 14 ? 'CNPJ' : 'CPF',
      identificationNumber: cardInfo?.document
    };

    const mp = new window.MercadoPago(config.key);

    const hash = await mp
      .createCardToken(payload)
      .then((result: { id: string }) => result.id)
      .catch((err: any) => {
        this.sendToSentry({ ...err, tag: 'MP' });
        return '';
      });

    return {
      id: config.id,
      hash
    };
  }

  private async generateHashIugu(config: IHashConfig, cardInfo: ICardInfo): Promise<IHashResult> {
    if (!window?.Iugu) {
      return Promise.resolve({
        id: config.id,
        hash: ''
      });
    }
    const names = cardInfo.name.split(/\s+/);

    const name = names[0];
    const lastName = names[names.length - 1];

    return new Promise<IHashResult>((resolve) => {
      const requesMaxtime = setTimeout(() => {
        this.sendToSentry({ tag: 'IUGU-empty' });
        resolve({
          id: config.id,
          hash: ''
        });
      }, WAIT_FOR_CARD_MAX_TIME);

      window.Iugu.setAccountID(config.key);
      window.Iugu.setTestMode(IS_DEVELOPMENT);

      const card = window.Iugu.CreditCard(cardInfo.number, cardInfo.month, cardInfo.year, name, lastName, cardInfo.cvv);

      window.Iugu.createPaymentToken(card, (body: any) => {
        clearTimeout(requesMaxtime);
        if (body?.errors?.timeout === 'operation_timeout') {
          Sentry.setTag('deepError', 'IUGU_TOKENIZATION_TIMEOUT');
        }
        if (!body || body.errors) {
          this.sendToSentry({ base: JSON.stringify(body) || '', tag: 'IUGU' });
          resolve({
            id: config.id,
            hash: ''
          });
          return;
        }
        resolve({
          id: config.id,
          hash: body.id as string
        });
      });
    });
  }

  private async generateHashPagarme(config: IHashConfig, cardInfo: ICardInfo): Promise<IHashResult> {
    try {
      /* eslint-disable camelcase */
      const payload = {
        card: {
          number: cardInfo?.number,
          holder_name: cardInfo?.name,
          exp_month: cardInfo?.month,
          exp_year: cardInfo?.year,
          cvv: cardInfo?.cvv
        },
        type: 'card'
      };

      const response: AxiosResponse = await axios.post(`https://api.pagar.me/core/v5/tokens?appId=${config.key}`, payload, {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json'
        }
      });

      return {
        id: config.id,
        hash: response?.data?.id
      };
    } catch (error) {
      console.error(error);
      return {
        id: config.id,
        hash: ''
      };
    }
    /* eslint-enable camelcase */
  }

  private sendToSentry(data: any) {
    Sentry.setTag('error', 'empty-token');

    data = { ...data, date: new Date() };
    Sentry.addBreadcrumb({
      category: 'info',
      level: Sentry.Severity.Error,
      data
    });

    Sentry.captureMessage(`[${data.tag ?? ''}] Erro ao gerar hash card!!!`, {
      level: Sentry.Severity.Error,
      extra: data
    });
  }
}

export const cardHashService = new CardHashService(GATEWAY_SECRETS[ENV]);
